import React, { useState, useEffect } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import DataTable from "../ui/DataTable";
import NotificationsService from '../../services/NotificationsService';
import Modal from '../ui/Modal';

const SmtpSetupView = ({ notificationEnums }) => {
    const [smtpSetups, setSmtpSetups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        setUpName: '',
        smtpHostName: '',
        smtpAuthUser: '',
        smtpAuthPassword: '',
        allowAuthentication: 'YES',
        isDefault: 'NO',
        port: '587',
        sendingEmail: '',
        senderName: '',
        allowNotifications: 'YES',
        primaryColor: '#007bff', // Default primary color
        secondaryColor: '#6c757d', // Default secondary color
        footerText: '' // Default empty footer text
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [modalTitle, setModalTitle] = useState('New SMTP Setup');

    useEffect(() => {
        loadSmtpSetups();
    }, []);

    const loadSmtpSetups = async () => {
        try {
            setLoading(true);
            const response = await NotificationsService.getSmtpSetups();

            if (response.success) {
                setSmtpSetups(response.data || []);
            } else {
                console.error('Failed to load SMTP setups:', response.message);
            }
        } catch (error) {
            console.error('Error loading SMTP setups:', error);
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.setUpName?.trim()) {
            newErrors.setUpName = 'Setup name is required';
        }

        if (!formData.smtpHostName?.trim()) {
            newErrors.smtpHostName = 'SMTP host is required';
        }

        if (formData.allowAuthentication === 'YES') {
            if (!formData.smtpAuthUser?.trim()) {
                newErrors.smtpAuthUser = 'SMTP auth user is required when authentication is enabled';
            }
            if (!formData.smtpAuthPassword?.trim() && !formData.id) {
                newErrors.smtpAuthPassword = 'SMTP auth password is required when authentication is enabled';
            }
        }

        if (!formData.port?.trim()) {
            newErrors.port = 'Port is required';
        } else if (!/^\d+$/.test(formData.port)) {
            newErrors.port = 'Port must be a number';
        }

        if (!formData.sendingEmail?.trim()) {
            newErrors.sendingEmail = 'Sending email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.sendingEmail)) {
            newErrors.sendingEmail = 'Valid email is required';
        }

        if (!formData.senderName?.trim()) {
            newErrors.senderName = 'Sender name is required';
        }

        // Validate color fields
        if (!formData.primaryColor?.trim()) {
            newErrors.primaryColor = 'Primary color is required';
        } else if (!/^#[0-9A-Fa-f]{6}$/.test(formData.primaryColor)) {
            newErrors.primaryColor = 'Primary color must be a valid hex color (e.g., #007bff)';
        }

        if (!formData.secondaryColor?.trim()) {
            newErrors.secondaryColor = 'Secondary color is required';
        } else if (!/^#[0-9A-Fa-f]{6}$/.test(formData.secondaryColor)) {
            newErrors.secondaryColor = 'Secondary color must be a valid hex color (e.g., #6c757d)';
        }

        // Footer text is optional, no validation needed

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCheckboxChange = (name) => {
        setFormData(prev => ({
            ...prev,
            [name]: prev[name] === 'YES' ? 'NO' : 'YES'
        }));
    };

    const handleEdit = (setup) => {
        setModalTitle('Edit SMTP Setup');
        setFormData({
            id: setup.id,
            setUpName: setup.setUpName || '',
            smtpHostName: setup.smtpHostName || '',
            smtpAuthUser: setup.smtpAuthUser || '',
            smtpAuthPassword: '',
            allowAuthentication: setup.allowAuthentication || 'YES',
            isDefault: setup.isDefault || 'NO',
            port: setup.port || '587',
            sendingEmail: setup.sendingEmail || '',
            senderName: setup.senderName || '',
            allowNotifications: setup.allowNotifications || 'YES',
            primaryColor: setup.primaryColor || '#007bff',
            secondaryColor: setup.secondaryColor || '#6c757d',
            footerText: setup.footerText || ''
        });
        setShowModal(true);
        setErrors({});
        setShowPassword(false);
    };

    const handleView = async (setup) => {
        try {
            const response = await NotificationsService.getSmtpById({ ids: [setup.id] });
            if (response.success) {
                setModalTitle('View SMTP Setup');
                setFormData({
                    id: response.data.id,
                    setUpName: response.data.setUpName || '',
                    smtpHostName: response.data.smtpHostName || '',
                    smtpAuthUser: response.data.smtpAuthUser || '',
                    smtpAuthPassword: '********',
                    allowAuthentication: response.data.allowAuthentication || 'YES',
                    isDefault: response.data.isDefault || 'NO',
                    port: response.data.port || '587',
                    sendingEmail: response.data.sendingEmail || '',
                    senderName: response.data.senderName || '',
                    allowNotifications: response.data.allowNotifications || 'YES',
                    primaryColor: response.data.primaryColor || '#007bff',
                    secondaryColor: response.data.secondaryColor || '#6c757d',
                    footerText: response.data.footerText || ''
                });
                setShowModal(true);
                setErrors({});
                setShowPassword(false);
            }
        } catch (error) {
            console.error('Error viewing SMTP setup:', error);
        }
    };

    const handleDelete = (setup) => {
        confirmAlert({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete "${setup.setUpName}"? This action cannot be undone.`,
            buttons: [
                {
                    label: 'Yes, Delete',
                    onClick: async () => {
                        try {
                            setDeleting(true);
                            const response = await NotificationsService.deleteSmtpSetup({ ids: [setup.id] });

                            if (response.success) {
                                await loadSmtpSetups();
                            } else {
                                console.error('Failed to delete SMTP setup:', response.message);
                            }
                        } catch (error) {
                            console.error('Error deleting SMTP setup:', error);
                        } finally {
                            setDeleting(false);
                        }
                    }
                },
                {
                    label: 'Cancel',
                    onClick: () => {}
                }
            ]
        });
    };

    const handleSave = async () => {
        console.log('Saving form data:', formData);
        if (!validateForm()) {
            console.log('Validation failed:', errors);
            return;
        }

        try {
            setSaving(true);

            const payload = { ...formData };
            if (payload.id && (payload.smtpAuthPassword === '********' || payload.smtpAuthPassword === '')) {
                delete payload.smtpAuthPassword;
            }

            console.log('Sending payload:', payload);
            const response = payload.id
                ? await NotificationsService.saveSmtpSetup(payload)
                : await NotificationsService.saveSmtpSetup(payload);

            console.log('Save response:', response);
            if (response.success) {
                await loadSmtpSetups();
                handleCloseModal();
            } else {
                console.error('Failed to save SMTP setup:', response.message);
            }
        } catch (error) {
            console.error('Error saving SMTP setup:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleNewSetup = () => {
        console.log('Opening new SMTP setup modal');
        setModalTitle('New SMTP Setup');
        setFormData({
            id: null,
            setUpName: '',
            smtpHostName: '',
            smtpAuthUser: '',
            smtpAuthPassword: '',
            allowAuthentication: 'YES',
            isDefault: 'NO',
            port: '587',
            sendingEmail: '',
            senderName: '',
            allowNotifications: 'YES',
            primaryColor: '#007bff',
            secondaryColor: '#6c757d',
            footerText: ''
        });
        setShowModal(true);
        setErrors({});
        setShowPassword(false);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            id: null,
            setUpName: '',
            smtpHostName: '',
            smtpAuthUser: '',
            smtpAuthPassword: '',
            allowAuthentication: 'YES',
            isDefault: 'NO',
            port: '587',
            sendingEmail: '',
            senderName: '',
            allowNotifications: 'YES',
            primaryColor: '#007bff',
            secondaryColor: '#6c757d',
            footerText: ''
        });
        setErrors({});
        setShowPassword(false);
    };

    const columns = [
        {
            header: 'Setup Name',
            accessorFn: (row) => row.setUpName,
            id: 'setUpName',
            cell: (info) => info.getValue(),
        },
        {
            header: 'SMTP Host',
            accessorFn: (row) => row.smtpHostName,
            id: 'smtpHostName',
            cell: (info) => info.getValue(),
        },
        {
            header: 'Port',
            accessorFn: (row) => row.port,
            id: 'port',
            cell: (info) => info.getValue(),
        },
        {
            header: 'Sending Email',
            accessorFn: (row) => row.sendingEmail,
            id: 'sendingEmail',
            cell: (info) => info.getValue(),
        },
        {
            header: 'Auth',
            accessorFn: (row) => row.allowAuthentication,
            id: 'allowAuthentication',
            cell: (info) => {
                const val = info.getValue();
                return (
                    <span className={`status-badge ${val === 'YES' ? 'verified' : 'pending'}`}>
                        {val === 'YES' ? 'Enabled' : 'Disabled'}
                    </span>
                );
            },
        },
        {
            header: 'Default',
            accessorFn: (row) => row.isDefault,
            id: 'isDefault',
            cell: (info) => {
                const val = info.getValue();
                return val === 'YES' ? (
                    <span className="badge badge-primary">Default</span>
                ) : null;
            },
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: ({ row }) => (
                <div className="action-buttons">
                    <button
                        className="btn-sm btn-info"
                        onClick={() => handleView(row.original)}
                        title="View Details"
                    >
                        <i className="fas fa-eye"></i>
                    </button>
                    <button
                        className="btn-sm btn-primary"
                        onClick={() => handleEdit(row.original)}
                        title="Edit"
                        style={{ marginLeft: '5px' }}
                    >
                        <i className="fas fa-edit"></i>
                    </button>
                    <button
                        className="btn-sm btn-danger"
                        onClick={() => handleDelete(row.original)}
                        title="Delete"
                        style={{ marginLeft: '5px' }}
                    >
                        <i className="fas fa-trash"></i>
                    </button>
                </div>
            ),
        }
    ];

    return (
        <div className="smtp-setup-view">
            <div className="view-header">
                <h2>SMTP Mail Setups</h2>
                <button
                    className="btn-primary"
                    onClick={handleNewSetup}
                >
                    <i className="fas fa-plus"></i> New SMTP Setup
                </button>
            </div>

            <div className="table-container">
                <DataTable
                    columns={columns}
                    data={smtpSetups}
                    loading={loading}
                    emptyMessage="No SMTP setups found. Click 'New SMTP Setup' to create one."
                    pageSize={10}
                    onRowDoubleClick={(row) => handleView(row.original)}
                />
            </div>

            <Modal
                show={showModal}
                onClose={handleCloseModal}
                title={modalTitle}
                size="large"
                closeOnOutsideClick={false}
            >
                <div className="form-grid">
                    <div className="form-group">
                        <label htmlFor="setUpName">
                            Setup Name <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="setUpName"
                            name="setUpName"
                            value={formData.setUpName}
                            onChange={handleInputChange}
                            placeholder="e.g., Primary SMTP Server"
                            disabled={modalTitle === 'View SMTP Setup'}
                            className={errors.setUpName ? 'error' : ''}
                        />
                        {errors.setUpName && <span className="error-text">{errors.setUpName}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="smtpHostName">
                            SMTP Host <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="smtpHostName"
                            name="smtpHostName"
                            value={formData.smtpHostName}
                            onChange={handleInputChange}
                            placeholder="e.g., smtp.gmail.com"
                            disabled={modalTitle === 'View SMTP Setup'}
                            className={errors.smtpHostName ? 'error' : ''}
                        />
                        {errors.smtpHostName && <span className="error-text">{errors.smtpHostName}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="port">
                            Port <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="port"
                            name="port"
                            value={formData.port}
                            onChange={handleInputChange}
                            placeholder="e.g., 587 for TLS, 465 for SSL"
                            disabled={modalTitle === 'View SMTP Setup'}
                            className={errors.port ? 'error' : ''}
                        />
                        {errors.port && <span className="error-text">{errors.port}</span>}
                    </div>

                    <div className="form-group full-width">
                        <div className="checkbox-group">
                            <input
                                type="checkbox"
                                id="allowAuthentication"
                                checked={formData.allowAuthentication === 'YES'}
                                onChange={() => handleCheckboxChange('allowAuthentication')}
                                disabled={modalTitle === 'View SMTP Setup'}
                            />
                            <label htmlFor="allowAuthentication">Enable Authentication</label>
                        </div>
                    </div>

                    {formData.allowAuthentication === 'YES' && (
                        <>
                            <div className="form-group">
                                <label htmlFor="smtpAuthUser">
                                    SMTP Username <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="smtpAuthUser"
                                    name="smtpAuthUser"
                                    value={formData.smtpAuthUser}
                                    onChange={handleInputChange}
                                    placeholder="SMTP authentication username"
                                    disabled={modalTitle === 'View SMTP Setup'}
                                    className={errors.smtpAuthUser ? 'error' : ''}
                                />
                                {errors.smtpAuthUser && <span className="error-text">{errors.smtpAuthUser}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="smtpAuthPassword">
                                    SMTP Password {!formData.id && <span className="required">*</span>}
                                </label>
                                <div className="password-input">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="smtpAuthPassword"
                                        name="smtpAuthPassword"
                                        value={formData.smtpAuthPassword}
                                        onChange={handleInputChange}
                                        placeholder={formData.id ? "Leave blank to keep existing" : "SMTP password"}
                                        disabled={modalTitle === 'View SMTP Setup'}
                                        className={errors.smtpAuthPassword ? 'error' : ''}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={modalTitle === 'View SMTP Setup'}
                                    >
                                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                                {errors.smtpAuthPassword && <span className="error-text">{errors.smtpAuthPassword}</span>}
                                {formData.id && (
                                    <small className="hint">Leave blank to keep existing password</small>
                                )}
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <label htmlFor="sendingEmail">
                            Sending Email <span className="required">*</span>
                        </label>
                        <input
                            type="email"
                            id="sendingEmail"
                            name="sendingEmail"
                            value={formData.sendingEmail}
                            onChange={handleInputChange}
                            placeholder="e.g., noreply@yourdomain.com"
                            disabled={modalTitle === 'View SMTP Setup'}
                            className={errors.sendingEmail ? 'error' : ''}
                        />
                        {errors.sendingEmail && <span className="error-text">{errors.sendingEmail}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="senderName">
                            Sender Name <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="senderName"
                            name="senderName"
                            value={formData.senderName}
                            onChange={handleInputChange}
                            placeholder="e.g., Your Company Name"
                            disabled={modalTitle === 'View SMTP Setup'}
                            className={errors.senderName ? 'error' : ''}
                        />
                        {errors.senderName && <span className="error-text">{errors.senderName}</span>}
                    </div>

                    {/* New Color Fields */}
                    <div className="form-group">
                        <label htmlFor="primaryColor">
                            Primary Color <span className="required">*</span>
                        </label>
                        <div className="color-input-group">
                            <input
                                type="color"
                                id="primaryColorPicker"
                                value={formData.primaryColor}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    primaryColor: e.target.value
                                }))}
                                disabled={modalTitle === 'View SMTP Setup'}
                                style={{ width: '40px', height: '40px', cursor: modalTitle !== 'View SMTP Setup' ? 'pointer' : 'default' }}
                            />
                            <input
                                type="text"
                                id="primaryColor"
                                name="primaryColor"
                                value={formData.primaryColor}
                                onChange={handleInputChange}
                                placeholder="#007bff"
                                disabled={modalTitle === 'View SMTP Setup'}
                                className={errors.primaryColor ? 'error' : ''}
                                style={{ flex: 1 }}
                            />
                        </div>
                        {errors.primaryColor && <span className="error-text">{errors.primaryColor}</span>}
                        <small className="hint">Used for headers and primary elements in emails</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="secondaryColor">
                            Secondary Color <span className="required">*</span>
                        </label>
                        <div className="color-input-group">
                            <input
                                type="color"
                                id="secondaryColorPicker"
                                value={formData.secondaryColor}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    secondaryColor: e.target.value
                                }))}
                                disabled={modalTitle === 'View SMTP Setup'}
                                style={{ width: '40px', height: '40px', cursor: modalTitle !== 'View SMTP Setup' ? 'pointer' : 'default' }}
                            />
                            <input
                                type="text"
                                id="secondaryColor"
                                name="secondaryColor"
                                value={formData.secondaryColor}
                                onChange={handleInputChange}
                                placeholder="#6c757d"
                                disabled={modalTitle === 'View SMTP Setup'}
                                className={errors.secondaryColor ? 'error' : ''}
                                style={{ flex: 1 }}
                            />
                        </div>
                        {errors.secondaryColor && <span className="error-text">{errors.secondaryColor}</span>}
                        <small className="hint">Used for secondary elements and footers in emails</small>
                    </div>

                    <div className="form-group full-width">
                        <label htmlFor="footerText">
                            Footer Text
                        </label>
                        <textarea
                            id="footerText"
                            name="footerText"
                            value={formData.footerText}
                            onChange={handleInputChange}
                            placeholder="e.g., © 2024 Your Company. All rights reserved. | Contact: support@yourcompany.com"
                            disabled={modalTitle === 'View SMTP Setup'}
                            rows="3"
                            style={{ resize: 'vertical' }}
                        />
                        <small className="hint">This text will appear at the bottom of all emails sent using this setup</small>
                    </div>

                    <div className="form-group full-width">
                        <div className="checkbox-group">
                            <input
                                type="checkbox"
                                id="isDefault"
                                checked={formData.isDefault === 'YES'}
                                onChange={() => handleCheckboxChange('isDefault')}
                                disabled={modalTitle === 'View SMTP Setup'}
                            />
                            <label htmlFor="isDefault">Set as Default Configuration</label>
                        </div>
                    </div>

                    <div className="form-group full-width">
                        <div className="checkbox-group">
                            <input
                                type="checkbox"
                                id="allowNotifications"
                                checked={formData.allowNotifications === 'YES'}
                                onChange={() => handleCheckboxChange('allowNotifications')}
                                disabled={modalTitle === 'View SMTP Setup'}
                            />
                            <label htmlFor="allowNotifications">Enable Notifications</label>
                        </div>
                    </div>

                    {modalTitle !== 'View SMTP Setup' && (
                        <div className="form-actions full-width">
                            <button
                                className="btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                                {saving ? ' Saving...' : ' Save'}
                            </button>
                            <button
                                className="btn-secondary"
                                onClick={handleCloseModal}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default SmtpSetupView;